import {
  Archive,
  ClockCounterClockwise,
  Confetti,
  DownloadSimple,
  FileArrowUp,
  Funnel,
  GameController,
  GearSix,
  Guitar,
  ListBullets,
  LockKey,
  MusicNotes,
  Planet,
  PushPin,
  SpeakerHigh,
  SpeakerSlash,
  Trash,
  UploadSimple,
  X,
} from "@phosphor-icons/react";
import {
  useMemo,
  useRef,
  useState,
  type FocusEventHandler,
  type PointerEventHandler,
  type RefObject,
} from "react";
import { parsePaste } from "../services/importService";
import { SOUND_PACK_OPTIONS } from "../services/audio";
import type {
  PickerEntry,
  PickscapeProject,
  PickscapeSettings,
  SoundPackId,
} from "../types";
import type { ImportSource } from "./ImportDialog";

type BackstageTab = "list" | "filters" | "history" | "settings";

interface BackstagePanelProps {
  project: PickscapeProject;
  rememberOnDevice: boolean;
  collapsed: boolean;
  pinned: boolean;
  isMobile: boolean;
  openOnMobile: boolean;
  sidebarRef: RefObject<HTMLElement | null>;
  edgeToggleRef: RefObject<HTMLButtonElement | null>;
  mobileCloseRef: RefObject<HTMLButtonElement | null>;
  onPointerEnter: PointerEventHandler<HTMLElement>;
  onPointerLeave: PointerEventHandler<HTMLElement>;
  onFocusCapture: FocusEventHandler<HTMLElement>;
  onBlurCapture: FocusEventHandler<HTMLElement>;
  onRestoreDesktop: () => void;
  onTogglePinned: () => void;
  onCloseMobile: () => void;
  onChangeProject: (updater: (project: PickscapeProject) => PickscapeProject) => void;
  onReviewImport: (source: ImportSource) => void;
  onRestoreBackup: (file: File) => void;
  onRememberChange: (remember: boolean) => void;
  onDeleteRememberedData: () => void;
  onExportList: () => void;
  onExportHistory: () => void;
  onExportBackup: () => void;
  onUndoHistory: () => void;
  onClearHistory: () => void;
}

const tabs: Array<{
  id: BackstageTab;
  label: string;
  description: string;
  icon: typeof ListBullets;
}> = [
  { id: "list", label: "List", description: "Build the selection pool", icon: ListBullets },
  { id: "filters", label: "Filters", description: "Control draw eligibility", icon: Funnel },
  {
    id: "history",
    label: "Results",
    description: "Review previous winners",
    icon: ClockCounterClockwise,
  },
  { id: "settings", label: "Settings", description: "Tune the experience", icon: GearSix },
];

const soundPackIcons: Record<SoundPackId, typeof MusicNotes> = {
  cinematic: MusicNotes,
  party: Confetti,
  rock: Guitar,
  arcade: GameController,
};

function updateSettings(
  project: PickscapeProject,
  patch: Partial<PickscapeSettings>,
): PickscapeProject {
  return {
    ...project,
    settings: { ...project.settings, ...patch },
    updatedAt: new Date().toISOString(),
  };
}

export function BackstagePanel({
  project,
  rememberOnDevice,
  collapsed,
  pinned,
  isMobile,
  openOnMobile,
  sidebarRef,
  edgeToggleRef,
  mobileCloseRef,
  onPointerEnter,
  onPointerLeave,
  onFocusCapture,
  onBlurCapture,
  onRestoreDesktop,
  onTogglePinned,
  onCloseMobile,
  onChangeProject,
  onReviewImport,
  onRestoreBackup,
  onRememberChange,
  onDeleteRememberedData,
  onExportList,
  onExportHistory,
  onExportBackup,
  onUndoHistory,
  onClearHistory,
}: BackstagePanelProps) {
  const [activeTab, setActiveTab] = useState<BackstageTab>("list");
  const [pasteText, setPasteText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          project.entries
            .map((entry) => entry.category)
            .filter((category): category is string => Boolean(category)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [project.entries],
  );

  const reviewPaste = () => {
    const parsed = parsePaste(pasteText);
    if (parsed.rows.length === 0) return;
    onReviewImport({ kind: "parsed", parsed });
  };

  const selectFile = (file: File | undefined) => {
    if (!file) return;
    if (file.name.toLocaleLowerCase().endsWith(".json")) {
      onRestoreBackup(file);
    } else {
      onReviewImport({ kind: "file", file });
    }
  };

  const toggleEntry = (entryId: string) => {
    onChangeProject((current) => ({
      ...current,
      entries: current.entries.map((entry) =>
        entry.id === entryId ? { ...entry, enabled: !entry.enabled } : entry,
      ),
      updatedAt: new Date().toISOString(),
    }));
  };

  const removeEntry = (entryId: string) => {
    onChangeProject((current) => ({
      ...current,
      entries: current.entries.filter((entry) => entry.id !== entryId),
      updatedAt: new Date().toISOString(),
    }));
  };

  const toggleCategory = (category: string) => {
    onChangeProject((current) => {
      const selected = new Set(current.settings.selectedCategories);
      if (selected.has(category)) selected.delete(category);
      else selected.add(category);
      return updateSettings(current, { selectedCategories: Array.from(selected) });
    });
  };

  const renderedEntries = project.entries.slice(0, 220);
  const poolReady = project.entries.some((entry) => entry.enabled);
  const selectedSoundPack = project.settings.soundPack ?? "cinematic";
  const selectedSoundPackOption =
    SOUND_PACK_OPTIONS.find((option) => option.id === selectedSoundPack) ??
    SOUND_PACK_OPTIONS[0]!;

  return (
    <aside
      ref={sidebarRef}
      id="pickscape-backstage"
      className={`backstage ${openOnMobile ? "mobile-open" : ""}`}
      aria-label="Pickscape backstage"
      aria-hidden={isMobile && !openOnMobile}
      inert={isMobile && !openOnMobile ? true : undefined}
      data-collapsed={collapsed}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onFocusCapture={onFocusCapture}
      onBlurCapture={onBlurCapture}
    >
      <header className="backstage-header">
        <div className="backstage-brand">
          <span className="backstage-brand-mark" aria-hidden="true">
            <Planet size={22} weight="duotone" />
          </span>
          <div className="backstage-brand-copy">
            <span className="backstage-label">Backstage</span>
            <input
              className="list-name-input"
              value={project.listName}
              aria-label="List name"
              onChange={(event) =>
                onChangeProject((current) => ({
                  ...current,
                  listName: event.target.value,
                  updatedAt: new Date().toISOString(),
                }))
              }
            />
          </div>
        </div>

        <button
          ref={edgeToggleRef}
          type="button"
          className={`sidebar-lock desktop-only ${pinned ? "is-pinned" : ""}`}
          onClick={onTogglePinned}
          aria-controls="pickscape-backstage"
          aria-expanded="true"
          aria-pressed={pinned}
          aria-label={pinned ? "Turn sidebar auto-hide on" : "Turn sidebar auto-hide off"}
          title={
            pinned
              ? "Keep backstage open is on · click to use auto-hide"
              : "Auto-hide is on · click to keep backstage open"
          }
        >
          <span className="sidebar-lock-core">
            <PushPin size={18} weight={pinned ? "fill" : "regular"} />
          </span>
        </button>

        <button
          ref={mobileCloseRef}
          type="button"
          className="icon-button mobile-only"
          onClick={onCloseMobile}
          aria-label="Close backstage"
        >
          <X size={20} />
        </button>

        <button
          type="button"
          className="sidebar-restore desktop-only"
          onClick={onRestoreDesktop}
          aria-controls="pickscape-backstage"
          aria-expanded="false"
          aria-label="Show navigation"
          title="Hover to preview · click to show navigation"
          data-sidebar-restore
        >
          <Planet size={22} weight="duotone" />
        </button>
      </header>

      <nav className="backstage-tabs" aria-label="Backstage sections">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? "active" : ""}
              onClick={() => setActiveTab(tab.id)}
              aria-current={activeTab === tab.id ? "page" : undefined}
            >
              <Icon size={18} />
              <span className="backstage-tab-copy">
                <strong>{tab.label}</strong>
                <small>{tab.description}</small>
              </span>
            </button>
          );
        })}
      </nav>

      <div className="backstage-content">
        {activeTab === "list" ? (
          <section className="backstage-section" aria-labelledby="list-heading">
            <div className="section-heading-row">
              <div>
                <h2 id="list-heading">Your pool</h2>
                <p>{project.entries.length.toLocaleString()} entries</p>
              </div>
              {project.entries.length > 0 ? (
                <button
                  type="button"
                  className="text-button danger"
                  onClick={() =>
                    onChangeProject((current) => ({
                      ...current,
                      entries: [],
                      history: [],
                      settings: { ...current.settings, selectedCategories: [] },
                      updatedAt: new Date().toISOString(),
                    }))
                  }
                >
                  Clear
                </button>
              ) : null}
            </div>

            <label className="field">
              <span>Paste names or items</span>
              <textarea
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                placeholder={"One entry per line\nOr paste columns from a spreadsheet"}
                rows={5}
              />
              <small>Commas stay inside names. Tabs open column mapping.</small>
            </label>

            <div className="input-actions">
              <button
                type="button"
                className="primary-button"
                onClick={reviewPaste}
                disabled={!pasteText.trim()}
              >
                <ListBullets size={18} />
                Review paste
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadSimple size={18} />
                Upload
              </button>
              <input
                ref={fileInputRef}
                className="visually-hidden"
                type="file"
                aria-label="Choose list file"
                accept=".txt,.csv,.tsv,.xlsx,.json,text/plain,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/json"
                onChange={(event) => {
                  selectFile(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
            </div>

            <p className="format-note">
              TXT, CSV, TSV, XLSX, or a Pickscape backup. Files stay on this device.
            </p>

            {project.entries.length === 0 ? (
              <div className="empty-panel">
                <FileArrowUp size={30} />
                <strong>Add your first entries</strong>
                <p>Paste a list or import a file to prepare the stage.</p>
              </div>
            ) : (
              <div className="entry-list" aria-label="Imported entries">
                {renderedEntries.map((entry: PickerEntry) => (
                  <div className={`entry-row ${entry.enabled ? "" : "disabled"}`} key={entry.id}>
                    <label className="entry-toggle">
                      <input
                        type="checkbox"
                        checked={entry.enabled}
                        onChange={() => toggleEntry(entry.id)}
                        aria-label={`${entry.enabled ? "Disable" : "Enable"} ${entry.name}`}
                      />
                      <span>
                        <strong>{entry.name}</strong>
                        <small>
                          {entry.category ?? "Uncategorized"}
                          {entry.weight > 1 ? `, weight ${entry.weight}` : ""}
                        </small>
                      </span>
                    </label>
                    <button
                      type="button"
                      className="icon-button subtle"
                      onClick={() => removeEntry(entry.id)}
                      aria-label={`Remove ${entry.name}`}
                    >
                      <Trash size={17} />
                    </button>
                  </div>
                ))}
                {project.entries.length > renderedEntries.length ? (
                  <p className="list-limit-note">
                    Showing the first {renderedEntries.length}. All entries remain eligible.
                  </p>
                ) : null}
              </div>
            )}
          </section>
        ) : null}

        {activeTab === "filters" ? (
          <section className="backstage-section" aria-labelledby="filters-heading">
            <div className="section-heading-row">
              <div>
                <h2 id="filters-heading">Eligibility</h2>
                <p>Category filters never change stored data.</p>
              </div>
              {project.settings.selectedCategories.length > 0 ? (
                <button
                  type="button"
                  className="text-button"
                  onClick={() =>
                    onChangeProject((current) => updateSettings(current, { selectedCategories: [] }))
                  }
                >
                  Reset
                </button>
              ) : null}
            </div>

            {categories.length === 0 ? (
              <div className="empty-panel">
                <Funnel size={30} />
                <strong>No categories yet</strong>
                <p>Map a category column during import to filter the draw.</p>
              </div>
            ) : (
              <div className="category-grid">
                {categories.map((category) => {
                  const active =
                    project.settings.selectedCategories.length === 0 ||
                    project.settings.selectedCategories.includes(category);
                  const count = project.entries.filter(
                    (entry) => entry.category === category && entry.enabled,
                  ).length;
                  return (
                    <button
                      type="button"
                      className={active ? "category-option active" : "category-option"}
                      onClick={() => toggleCategory(category)}
                      key={category}
                      aria-pressed={active}
                    >
                      <span>{category}</span>
                      <strong>{count}</strong>
                    </button>
                  );
                })}
              </div>
            )}

            <fieldset className="settings-group">
              <legend>Repeat behavior</legend>
              <label className="radio-option">
                <input
                  type="radio"
                  name="repeat-mode"
                  checked={project.settings.repeatMode === "no-repeat"}
                  onChange={() =>
                    onChangeProject((current) => updateSettings(current, { repeatMode: "no-repeat" }))
                  }
                />
                <span>
                  <strong>No repeats</strong>
                  <small>Previous winners leave the eligible pool until results are cleared.</small>
                </span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="repeat-mode"
                  checked={project.settings.repeatMode === "repeat"}
                  onChange={() =>
                    onChangeProject((current) => updateSettings(current, { repeatMode: "repeat" }))
                  }
                />
                <span>
                  <strong>Allow repeats</strong>
                  <small>Every enabled entry returns for each new draw.</small>
                </span>
              </label>
            </fieldset>
          </section>
        ) : null}

        {activeTab === "history" ? (
          <section className="backstage-section" aria-labelledby="history-heading">
            <div className="section-heading-row">
              <div>
                <h2 id="history-heading">Results</h2>
                <p>{project.history.length.toLocaleString()} completed draws</p>
              </div>
              {project.history.length > 0 ? (
                <button type="button" className="text-button danger" onClick={onClearHistory}>
                  Clear
                </button>
              ) : null}
            </div>

            {project.history.length === 0 ? (
              <div className="empty-panel">
                <ClockCounterClockwise size={30} />
                <strong>No results yet</strong>
                <p>Completed draws appear here with their eligibility receipt.</p>
              </div>
            ) : (
              <>
                <div className="history-list">
                  {project.history
                    .slice()
                    .reverse()
                    .slice(0, 60)
                    .map((record) => (
                      <article className="history-item" key={record.id}>
                        <div>
                          <span>Draw {record.drawNumber}</span>
                          <strong>{record.winnerName}</strong>
                          <small>
                            {record.winnerCategory ?? "Uncategorized"}
                            {record.keptInPool ? " • Kept in pool" : ""}
                          </small>
                        </div>
                        <time dateTime={record.timestamp}>
                          {new Intl.DateTimeFormat(undefined, {
                            hour: "numeric",
                            minute: "2-digit",
                          }).format(new Date(record.timestamp))}
                        </time>
                      </article>
                    ))}
                </div>
                <div className="input-actions">
                  <button type="button" className="secondary-button" onClick={onUndoHistory}>
                    <Archive size={18} />
                    Undo last
                  </button>
                  <button type="button" className="secondary-button" onClick={onExportHistory}>
                    <DownloadSimple size={18} />
                    Export CSV
                  </button>
                </div>
              </>
            )}
          </section>
        ) : null}

        {activeTab === "settings" ? (
          <section className="backstage-section" aria-labelledby="settings-heading">
            <div className="section-heading-row">
              <div>
                <h2 id="settings-heading">Local settings</h2>
                <p>Pickscape has no account or cloud connection.</p>
              </div>
              <LockKey size={22} />
            </div>

            <label className="toggle-option">
              <span>
                <strong>Remember on this device</strong>
                <small>Stores the list and results in browser storage.</small>
              </span>
              <input
                type="checkbox"
                checked={rememberOnDevice}
                onChange={(event) => onRememberChange(event.target.checked)}
              />
            </label>

            <label className="toggle-option">
              <span>
                <strong>Reduce motion</strong>
                <small>Uses calm movement and a direct winner focus.</small>
              </span>
              <input
                type="checkbox"
                checked={project.settings.motionReduced}
                onChange={(event) =>
                  onChangeProject((current) =>
                    updateSettings(current, { motionReduced: event.target.checked }),
                  )
                }
              />
            </label>

            <fieldset className="settings-group sound-studio">
              <legend>Sound experience</legend>

              <label className="toggle-option">
                <span>
                  <strong>Sound effects</strong>
                  <small>Launch, lock, winner music, applause, and crowd energy.</small>
                </span>
                <span className="toggle-control">
                  {project.settings.soundEnabled ? (
                    <SpeakerHigh size={17} aria-hidden="true" />
                  ) : (
                    <SpeakerSlash size={17} aria-hidden="true" />
                  )}
                  <input
                    type="checkbox"
                    checked={project.settings.soundEnabled}
                    onChange={(event) =>
                      onChangeProject((current) =>
                        updateSettings(current, { soundEnabled: event.target.checked }),
                      )
                    }
                  />
                </span>
              </label>

              <div className="sound-pack-grid" aria-label="Sound pack">
                {SOUND_PACK_OPTIONS.map((option) => {
                  const Icon = soundPackIcons[option.id];
                  const active = selectedSoundPack === option.id;
                  return (
                    <button
                      type="button"
                      key={option.id}
                      className={`sound-pack-card ${active ? "active" : ""}`}
                      onClick={() =>
                        onChangeProject((current) =>
                          updateSettings(current, { soundPack: option.id }),
                        )
                      }
                      aria-label={`${option.label} sound pack`}
                      aria-pressed={active}
                    >
                      <span className="sound-pack-icon" aria-hidden="true">
                        <Icon size={19} weight="duotone" />
                      </span>
                      <strong>{option.label}</strong>
                      <em>{option.character}</em>
                    </button>
                  );
                })}
              </div>
              <p className="sound-pack-description">
                <strong>{selectedSoundPackOption.label}:</strong>{" "}
                {selectedSoundPackOption.description}
              </p>
            </fieldset>

            <label className="field">
              <span>Appearance</span>
              <select
                value={project.settings.appearance}
                onChange={(event) =>
                  onChangeProject((current) =>
                    updateSettings(current, {
                      appearance: event.target.value as PickscapeSettings["appearance"],
                    }),
                  )
                }
              >
                <option value="dark">Midnight</option>
                <option value="light">Daylight</option>
                <option value="system">Use device setting</option>
              </select>
            </label>

            <div className="export-actions">
              <button type="button" className="secondary-button" onClick={onExportList}>
                <DownloadSimple size={18} />
                Export list CSV
              </button>
              <button type="button" className="secondary-button" onClick={onExportBackup}>
                <DownloadSimple size={18} />
                Export backup
              </button>
              {rememberOnDevice ? (
                <button
                  type="button"
                  className="secondary-button danger-outline"
                  onClick={onDeleteRememberedData}
                >
                  <Trash size={18} />
                  Delete saved data
                </button>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>

      <footer className="backstage-footer">
        <div className="backstage-status">
          <span className={`backstage-status-dot ${poolReady ? "ready" : ""}`} aria-hidden="true" />
          <span>
            <strong>{poolReady ? "Pool ready" : "Waiting for entries"}</strong>
            <small>{project.entries.length.toLocaleString()} entries loaded</small>
          </span>
        </div>
        <div className="backstage-storage">
          <span>Storage</span>
          <strong>{rememberOnDevice ? "Remembered" : "Local session"}</strong>
        </div>
      </footer>
    </aside>
  );
}
